import { useState, useEffect, useRef, isValidElement, cloneElement, Children } from "react";
import { createCustomEqual } from "fast-equals";
import { isLatLngLiteral } from "@googlemaps/typescript-guards";

const deepCompareEqualsForMaps = createCustomEqual((deepEqual) => (a, b) => {
    if (
      isLatLngLiteral(a) ||
      a instanceof window.google.maps.LatLng ||
      isLatLngLiteral(b) ||
      b instanceof window.google.maps.LatLng
    ) {
      return new window.google.maps.LatLng(a).equals(new window.google.maps.LatLng(b));
    }
    return deepEqual(a, b);
  });

  function useDeepCompareMemoize(value) {
    const ref = useRef();

    if (!deepCompareEqualsForMaps(value, ref.current)) {
      ref.current = value;
    }
    return ref.current;
  }

  function useDeepCompareEffectForMaps(callback, dependencies) {
    useEffect(callback, dependencies.map(useDeepCompareMemoize));
  }


const Map = ({ onClick, onIdle, children, style, ...options }) => {
    // [START maps_react_map_component_add_map_hooks]
    const ref = useRef(null);
    const [map, setMap] = useState();

    function checkCenterChanged(map) {
        if(map.getZoom() >= options.zoomBreakpointAreaSearch) {
            if(typeof options.setShowAreaSearchBt === "function") {
                //options.setShowAreaSearchBt(true);
                options.setAreaSearchButtonDisabled(false);
            }
        }
        else {
            if(typeof options.setShowAreaSearchBt === "function") {
                //options.setShowAreaSearchBt(false);
                options.setAreaSearchButtonDisabled(true);
            }
        }
    }

   useEffect(() => {
      if (ref.current && !map) {
        window.gmap = new window.google.maps.Map(ref.current, {})
        setMap(window.gmap);
      }
    }, [ref, map]);
    // [END maps_react_map_component_add_map_hooks]
    // [START maps_react_map_component_options_hook]
    // because React does not do deep comparisons, a custom hook is used
    // see discussion in https://github.com/googlemaps/js-samples/issues/946
    useDeepCompareEffectForMaps(() => {
      if (map) {
        map.setOptions(options);
      }
    }, [map, options]);
    // [END maps_react_map_component_options_hook]
    // [START maps_react_map_component_event_hooks]
   useEffect(() => {
      if (map) {
        ["click", "idle"].forEach((eventName) =>
        window.google.maps.event.clearListeners(map, eventName)
        );
        if (onClick) {
          map.addListener("click", onClick);
        }
        if (onIdle) {
          map.addListener("idle", () => onIdle(map));
        }
        map.addListener("dragend", () => checkCenterChanged(map));
        map.addListener("zoom_changed", () => checkCenterChanged(map));
      }
    }, [map, onClick, onIdle]);
    // [END maps_react_map_component_event_hooks]
    // [START maps_react_map_component_return]
    return (
      <>
        <div ref={ref} style={style} />
        {Children.map(children, (child) => {
          if (isValidElement(child)) {
            // set the map prop on the child component
            return cloneElement(child, { map });
          }
        })}
      </>
    );
    // [END maps_react_map_component_return]
  };

  export default Map;